import { Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { ASSETS } from "@/lib/assets";
import {
  ArrowUpRight,
  Cpu,
  Fingerprint,
  Layers,
  ShieldCheck,
  KeyRound,
  Workflow,
  Repeat,
  CheckCircle2,
  Lock,
} from "lucide-react";

const Section = ({ children, className = "", testid }) => (
  <section data-testid={testid} className={`py-24 md:py-32 ${className}`}>
    <div className="max-w-7xl mx-auto px-6 md:px-8">{children}</div>
  </section>
);

const Hero = () => (
  <section className="pt-32 md:pt-40 pb-16 bg-radial-soft relative overflow-hidden" data-testid="tech-hero">
    <div className="absolute inset-0 bg-grid opacity-50 pointer-events-none" />
    <div className="max-w-7xl mx-auto px-6 md:px-8 relative">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#06102A]/65">
          Technology
        </p>
      </Reveal>
      <Reveal delay={80}>
        <h1 className="mt-6 font-display text-5xl md:text-6xl lg:text-7xl font-medium leading-[1.02] tracking-tight text-[#06102A] max-w-4xl">
          Identity-Bound Custody Infrastructure
        </h1>
      </Reveal>
      <Reveal delay={140}>
        <p className="mt-7 max-w-2xl text-lg text-[#06102A]/70">
          A precision-engineered architecture that separates identity, key custody, and governance
          across hardware boundaries — and forces them to agree before any signature is ever
          produced.
        </p>
      </Reveal>
    </div>
  </section>
);

const Block = ({ kicker, title, bullets, image, reverse = false, accent = "#3B82F6" }) => (
  <div className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center`}>
    <div className={`lg:col-span-6 ${reverse ? "lg:order-2" : ""}`}>
      <Reveal>
        <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#06102A]/65">
          {kicker}
        </p>
      </Reveal>
      <Reveal delay={80}>
        <h2 className="mt-5 font-display text-4xl md:text-5xl font-medium leading-tight text-[#06102A]">
          {title}
        </h2>
      </Reveal>
      <Reveal delay={140}>
        <ul className="mt-8 space-y-4">
          {bullets.map((b) => (
            <li key={b} className="flex items-start gap-3">
              <span
                className="mt-2 h-1.5 w-6 rounded-full shrink-0"
                style={{ background: accent }}
              />
              <p className="text-[15px] text-[#06102A]/85 leading-relaxed">{b}</p>
            </li>
          ))}
        </ul>
      </Reveal>
    </div>
    <div className={`lg:col-span-6 ${reverse ? "lg:order-1" : ""}`}>
      <Reveal delay={120}>
        <div className="relative aspect-[5/4] rounded-3xl overflow-hidden"
          style={{
            background:
              "radial-gradient(120% 80% at 70% 20%, #FFFFFF 0%, #EEF0F4 55%, #DCDFE6 100%)",
            boxShadow:
              "0 40px 80px -30px rgba(6,16,42,0.25), inset 0 1px 0 rgba(255,255,255,0.7)",
          }}
        >
          <div className="absolute inset-0 bg-grid opacity-50" />
          <div
            className="absolute -top-16 -right-16 h-72 w-72 rounded-full pointer-events-none"
            style={{
              background:
                "radial-gradient(circle, rgba(59,130,246,0.20) 0%, transparent 65%)",
            }}
          />
          <div
            className="absolute bottom-0 left-0 h-40 w-60 pointer-events-none"
            style={{
              background:
                "radial-gradient(circle, rgba(249,115,22,0.14) 0%, transparent 60%)",
            }}
          />
          {image && (
            <img
              src={image}
              alt={title}
              className="absolute inset-0 w-full h-full object-contain p-10"
              style={{
                filter:
                  "drop-shadow(0 25px 30px rgba(6,16,42,0.22))",
              }}
            />
          )}
        </div>
      </Reveal>
    </div>
  </div>
);

export default function Technology() {
  return (
    <main className="bg-white" data-testid="technology-page">
      <Hero />

      <Section testid="tech-card-section">
        <Block
          kicker="Biometric Security Device · Layer 01"
          title="A personal HSM you carry."
          bullets={[
            "EAL6+ secure element",
            "On-device biometric matching — fingerprint never leaves the device",
            "No cloud biometrics, no seed phrases",
            "Physical user presence required for every sensitive action",
            "Currently deployed as Vaultavo's flagship biometric smart card form factor",
            "Recovery model designed for institutions and individuals",
          ]}
          image={ASSETS.cardNavy}
        />
      </Section>

      <Section testid="tech-form-factor-section" className="bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#06102A]/65">
                Form-Factor Agnostic Identity Infrastructure
              </p>
            </Reveal>
            <Reveal delay={80}>
              <h2 className="mt-5 font-display text-4xl md:text-5xl font-medium leading-tight text-[#06102A]">
                One architecture. Many secure devices.
              </h2>
            </Reveal>
            <Reveal delay={140}>
              <p className="mt-6 text-[#06102A]/70 leading-relaxed">
                Vaultavo's identity layer is not bound to a single hardware form. The Vaultavo
                Biometric Security Device is currently deployed as a biometric smart card — a
                familiar, durable, and EMV-ready format. The underlying architecture, however, is
                designed to evolve across institutional, enterprise, government, and identity
                ecosystems.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={120}>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  { t: "Biometric smart card", note: "Flagship deployment", active: true },
                  { t: "Authentication tokens" },
                  { t: "Enterprise security devices" },
                  { t: "Wearables" },
                  { t: "Embedded hardware modules" },
                  { t: "Government identity devices" },
                  { t: "Industrial secure devices" },
                  { t: "Specialised custody hardware" },
                ].map((f) => (
                  <div
                    key={f.t}
                    className={`rounded-2xl p-5 border ${
                      f.active
                        ? "border-[#06102A] bg-[#06102A] text-white"
                        : "border-[#06102A]/10 bg-white text-[#06102A]"
                    }`}
                  >
                    <p
                      className={`text-[10px] uppercase tracking-[0.22em] font-semibold ${
                        f.active ? "text-white/65" : "text-[#06102A]/50"
                      }`}
                    >
                      {f.note || "Future form factor"}
                    </p>
                    <p className="mt-3 text-sm font-medium leading-snug">{f.t}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </Section>

      <Section testid="tech-megavault-section" className="bg-[#FAFBFC]">
        <Block
          reverse
          accent="#06102A"
          kicker="MegaVault™ · Layer 02"
          title="Hardware segregation, by design."
          bullets={[
            "Isolated secure-chip array",
            "Per-user key segregation — no pooled custody",
            "No zone keys; one customer cannot impact another",
            "Hardware-to-hardware encrypted communication",
            "Quantum-ready cryptographic modularity",
          ]}
          image={ASSETS.megaVault}
        />
      </Section>

      <Section testid="tech-platform-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#06102A]/65">
                Digital Platform · Layer 03
              </p>
            </Reveal>
            <Reveal delay={80}>
              <h2 className="mt-5 font-display text-4xl md:text-5xl font-medium leading-tight text-[#06102A]">
                Policy as a control plane.
              </h2>
            </Reveal>
            <Reveal delay={140}>
              <ul className="mt-8 space-y-4">
                {[
                  "Initiator / verifier / approver workflows",
                  "Limits, approvals, time windows, and dual-control",
                  "Full audit trails — non-repudiable",
                  "KYC / AML / Travel Rule integrations",
                  "APIs for treasury, ledger, and core banking systems",
                  "Never stores private keys. Never stores biometrics. Never signs.",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-3">
                    <span className="mt-2 h-1.5 w-6 rounded-full bg-[#F97316]" />
                    <p className="text-[15px] text-[#06102A]/85 leading-relaxed">{b}</p>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>

          <div className="lg:col-span-6">
            <Reveal delay={120}>
              <div
                className="relative aspect-[5/4] rounded-3xl overflow-hidden"
                style={{
                  background:
                    "radial-gradient(120% 80% at 70% 20%, #FFFFFF 0%, #EEF0F4 55%, #DCDFE6 100%)",
                  boxShadow:
                    "0 40px 80px -30px rgba(6,16,42,0.25), inset 0 1px 0 rgba(255,255,255,0.7)",
                }}
              >
                <div className="absolute inset-0 bg-grid opacity-50" />
                <div
                  className="absolute -top-16 -right-16 h-72 w-72 rounded-full pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(circle, rgba(59,130,246,0.20) 0%, transparent 65%)",
                  }}
                />
                <img
                  src={ASSETS.platform}
                  alt="Vaultavo Digital Platform"
                  data-testid="tech-platform-image"
                  className="absolute inset-0 w-full h-full object-contain p-6 md:p-8"
                  style={{
                    filter: "drop-shadow(0 25px 30px rgba(6,16,42,0.22))",
                  }}
                />
              </div>
            </Reveal>
          </div>
        </div>
      </Section>

      <Section testid="tech-lifecycle-section" className="bg-[#FAFBFC]">
        <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#06102A]/65">
          Transaction Lifecycle
        </p>
        <h2 className="mt-5 font-display text-4xl md:text-5xl font-medium leading-tight text-[#06102A] max-w-7xl">
          Identity → Hardware → Policy → Blockchain.
        </h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-5">
          {[
            { i: Fingerprint, t: "Identity", d: "Biometric verification on the Smart Card." },
            { i: Cpu, t: "Hardware", d: "Signing inside the MegaVault secure-chip array." },
            { i: ShieldCheck, t: "Policy", d: "Workflow validation by the Digital Platform." },
            { i: Workflow, t: "Blockchain", d: "Signed transaction is broadcast." },
          ].map((s, i) => (
            <Reveal key={s.t} delay={i * 90}>
              <div className="rounded-2xl bg-white border border-[#06102A]/10 p-6 h-full">
                <s.i className="h-6 w-6 text-[#06102A]" strokeWidth={1.5} />
                <p className="mt-5 font-display text-lg text-[#06102A] font-medium">{s.t}</p>
                <p className="mt-2 text-sm text-[#06102A]/70 leading-relaxed">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section testid="tech-recovery-section">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#06102A]/65">
              Recovery & Continuity
            </p>
            <h2 className="mt-5 font-display text-4xl md:text-5xl font-medium leading-tight text-[#06102A]">
              Recovery without seed phrases.
            </h2>
            <ul className="mt-8 space-y-4">
              {[
                { i: Repeat, t: "Biometric-secured backups" },
                { i: KeyRound, t: "Successor controls" },
                { i: Lock, t: "Duress fingerprint" },
                { i: Layers, t: "Policy-controlled inheritance" },
              ].map((b) => (
                <li key={b.t} className="flex items-center gap-3">
                  <b.i className="h-4 w-4 text-[#3B82F6]" />
                  <p className="text-[15px] text-[#06102A]">{b.t}</p>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#06102A]/65">
              Quantum-Ready
            </p>
            <h2 className="mt-5 font-display text-4xl md:text-5xl font-medium leading-tight text-[#06102A]">
              Architected for the next decade of cryptography.
            </h2>
            <p className="mt-6 text-[#06102A]/70 leading-relaxed">
              Vaultavo's modular cryptography, hardware boundaries, and policy abstractions allow
              post-quantum primitives to be adopted as a configuration change — not a
              re-architecture.
            </p>
            <div className="mt-8 inline-flex items-center gap-2 text-sm text-[#06102A]">
              <CheckCircle2 className="h-4 w-4 text-[#3B82F6]" /> PQ-ready by design
            </div>
          </div>
        </div>
      </Section>

      <section className="py-20 bg-[#06102A] text-white text-center" data-testid="tech-cta">
        <div className="max-w-4xl mx-auto px-6">
          <h3 className="font-display text-3xl md:text-5xl font-medium leading-tight">
            See the architecture in motion.
          </h3>
          <Link
            to="/contact"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-[#06102A] text-sm font-medium px-6 py-3 hover:-translate-y-0.5 transition-all"
            data-testid="tech-cta-demo"
          >
            Request a Demo <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </main>
  );
}
